"use client";

import { type RefObject, useEffect, useState } from "react";

type UseElementInViewOptions = {
  rootMargin?: string;
  threshold?: number;
};

/**
 * Tracks whether a DOM element is visible in the viewport.
 * Defaults to `true` when the ref has no mounted target (sticky helpers stay hidden).
 */
export function useElementInView(
  ref: RefObject<HTMLElement | null>,
  deps: unknown[] = [],
  options?: UseElementInViewOptions,
) {
  const [isInView, setIsInView] = useState(true);

  useEffect(() => {
    const target = ref.current;
    if (!target) {
      setIsInView(true);
      return;
    }

    const scrollRoot = document.querySelector(
      "[data-scroll-container]",
    ) as HTMLElement | null;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
      },
      {
        threshold: options?.threshold ?? 0,
        rootMargin: options?.rootMargin ?? "0px 0px -8px 0px",
        root: scrollRoot,
      },
    );

    observer.observe(target);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, options?.rootMargin, options?.threshold, ...deps]);

  return isInView;
}
